<template>
  <div class="editor-toolbar-wrapper">
    <!-- 백업 복구 배너: backupRestored가 true일 때 툴바 위에 표시 -->
    <a-alert
      v-if="backupRestored"
      type="warning"
      show-icon
      style="margin-bottom: 8px; border-radius: 6px;"
      message="복구 가능한 변경사항이 있습니다"
      description="이전 세션에서 저장되지 않은 변경사항이 감지되었습니다. 복구하시겠습니까?"
    >
      <template #action>
        <div style="display: flex; gap: 8px; margin-top: 4px;">
          <a-button size="small" type="primary" class="yellow-button" @click="handleApplyBackup">
            복구
          </a-button>
          <a-button size="small" @click="handleDiscardBackup">
            무시
          </a-button>
        </div>
      </template>
    </a-alert>

    <!-- 툴바 본체 -->
    <div class="editor-toolbar" style="display: flex; align-items: center; gap: 12px;">
      <!-- 미저장 변경사항 표시 -->
      <div v-if="isDirty" class="dirty-indicator" style="display: flex; align-items: center; gap: 6px;">
        <span
          style="
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #fa8c16;
            display: inline-block;
          "
        />
        <span style="font-size: 13px; color: #fa8c16;">저장되지 않은 변경사항</span>
      </div>

      <!-- 미리보기 버튼 -->
      <a-button @click="openPreview" style="display: flex; align-items: center; gap: 4px;">
        <template #icon>
          <eye-outlined />
        </template>
        미리보기
      </a-button>

      <!-- 저장 에러 배지 + 재시도 버튼 -->
      <div v-if="saveError" style="display: flex; align-items: center; gap: 8px;">
        <!-- 실패 섹션 개수 툴팁 포함 배지 -->
        <a-tooltip :title="failedSectionsTooltip" placement="bottom">
          <a-badge
            :count="failedSections.length || '!'"
            :color="'#ff4d4f'"
          >
            <span
              style="
                display: inline-flex;
                align-items: center;
                padding: 2px 8px;
                background: #fff1f0;
                border: 1px solid #ff4d4f;
                border-radius: 4px;
                font-size: 12px;
                color: #cf1322;
                cursor: default;
              "
            >
              저장 오류
            </span>
          </a-badge>
        </a-tooltip>

        <!-- 재시도 버튼: 저장 중일 때 스피너 표시 -->
        <a-spin v-if="saveStatus === 'saving'" size="small" />
        <a-button
          v-else
          danger
          size="small"
          :disabled="saveStatus === 'saving'"
          @click="handleRetry"
        >
          재시도 ({{ failedSections.length }}개)
        </a-button>
      </div>

      <!-- 홈페이지 캐시 초기화 버튼 -->
      <a-button
        :loading="cacheInvalidating"
        @click="handleInvalidateCache"
      >
        캐시 초기화
      </a-button>

      <!-- 저장 버튼 -->
      <a-button
        class="yellow-button"
        type="primary"
        :loading="saveStatus === 'saving'"
        :disabled="!isDirty && saveStatus !== 'error'"
        @click="handleSave"
      >
        <!-- 저장 상태에 따른 버튼 텍스트 -->
        <template v-if="saveStatus === 'saving'">저장 중...</template>
        <template v-else-if="saveStatus === 'saved'">저장 완료</template>
        <template v-else-if="saveStatus === 'error'">다시 저장</template>
        <template v-else>저장</template>
      </a-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { EyeOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { HomepageApi } from '../api';

export default {
  name: 'EditorToolbar',

  components: {
    EyeOutlined,
  },

  data() {
    return {
      // 캐시 초기화 요청 진행 중 여부
      cacheInvalidating: false,
    };
  },

  computed: {
    ...mapGetters('homepageEditor', ['isDirty']),

    // 저장 상태
    saveStatus() {
      return this.$store.state.homepageEditor.saveStatus;
    },

    // 마지막 저장 에러 메시지
    saveError() {
      return this.$store.state.homepageEditor.saveError;
    },

    // 저장 실패 섹션 목록
    failedSections() {
      return this.$store.state.homepageEditor.failedSections;
    },

    // localStorage 백업 복구 가능 여부
    backupRestored() {
      return this.$store.state.homepageEditor.backupRestored;
    },

    // 재시도 버튼 툴팁: 실패 섹션 목록 표시
    failedSectionsTooltip() {
      if (!this.failedSections.length) return '저장 실패';
      return `실패한 섹션: ${this.failedSections.join(', ')}`;
    },
  },

  methods: {
    ...mapActions('homepageEditor', [
      'saveCurrentPage',
      'retryFailedSections',
      'applyBackup',
      'discardBackup',
    ]),

    // 홈페이지 미리보기 (새 탭으로 열기)
    openPreview() {
      const previewUrl = process.env.VUE_APP_HOMEPAGE_PREVIEW_URL || 'https://test.certi.life/';
      window.open(previewUrl, '_blank');
    },

    // 저장 버튼 핸들러
    async handleSave() {
      try {
        await this.saveCurrentPage();
        message.success('홈페이지 내용이 저장되었습니다.');
      } catch (error) {
        message.error(error.message || '저장 중 오류가 발생했습니다. 다시 시도해 주세요.');
        console.error('[EditorToolbar] 저장 실패:', error);
      }
    },

    // 실패 섹션 재시도 버튼 핸들러
    async handleRetry() {
      try {
        await this.retryFailedSections();
        message.success('재시도 저장이 완료되었습니다.');
      } catch (error) {
        message.error(error.message || '재시도 중 오류가 발생했습니다.');
        console.error('[EditorToolbar] 재시도 실패:', error);
      }
    },

    // 백업 복구 버튼 핸들러
    handleApplyBackup() {
      this.applyBackup();
      message.success('이전 변경사항이 복구되었습니다.');
    },

    // 백업 무시 버튼 핸들러
    handleDiscardBackup() {
      this.discardBackup();
      message.info('백업을 무시했습니다.');
    },

    // 홈페이지 Redis 캐시 초기화 핸들러
    async handleInvalidateCache() {
      this.cacheInvalidating = true;
      try {
        await HomepageApi.invalidateHomepageCache();
        message.success('홈페이지 캐시가 초기화되었습니다. 변경 사항은 web 재배포 후 반영됩니다.');
      } catch (error) {
        console.error('[EditorToolbar] 캐시 초기화 실패:', error);
        message.error('캐시 초기화에 실패했습니다.');
      } finally {
        this.cacheInvalidating = false;
      }
    },
  },
};
</script>
